import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { X, Save, ArrowLeft } from "lucide-react";
import WYSIWYGEditor from "./WYSIWYGEditor";

interface VisualPostEditorProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  initialPost?: any;
}

export default function VisualPostEditor({ onSuccess, onCancel, initialPost }: VisualPostEditorProps) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [category, setCategory] = useState("IA & Tecnologia");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [status, setStatus] = useState<"draft" | "pending_review" | "published">("draft");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  // Fetch available categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("posts")
        .select("category");

      if (!error && data) {
        const uniqueCategories = [...new Set(data.map(post => post.category))].sort();
        setAvailableCategories(uniqueCategories);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (initialPost) {
      // Fetch full post data including content
      const fetchFullPost = async () => {
        const { data, error } = await supabase
          .from("posts")
          .select("*")
          .eq("id", initialPost.id)
          .single();

        if (error) {
          toast.error("Erro ao carregar post");
          return;
        }

        setTitle(data.title || "");
        setContent(data.content || "");
        setMetaDescription(data.meta_description || "");
        setCategory(data.category || "");
        setCoverImageUrl(data.cover_image_url || "");
        setKeywords(data.keywords || []);
        setStatus(data.status || "draft");

        // Check if category is not in the available list (custom category)
        if (data.category && !availableCategories.includes(data.category)) {
          setIsCustomCategory(true);
        }
      };

      fetchFullPost();
    }
  }, [initialPost, availableCategories]);

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
      setHasChanges(true);
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter(k => k !== keyword));
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }

    if (!content.trim()) {
      toast.error("Conteúdo é obrigatório");
      return;
    }

    if (!metaDescription.trim()) {
      toast.error("Meta descrição é obrigatória");
      return;
    }

    setIsSubmitting(true);

    try {
      const slug = generateSlug(title);

      const postData = {
        title,
        slug,
        content,
        meta_description: metaDescription,
        category,
        cover_image_url: coverImageUrl || null,
        keywords,
        status,
      };

      if (initialPost) {
        const { error } = await supabase
          .from("posts")
          .update(postData)
          .eq("id", initialPost.id);

        if (error) throw error;
        toast.success("Post atualizado com sucesso!");
      } else {
        const { error } = await supabase
          .from("posts")
          .insert([postData]);

        if (error) throw error;
        toast.success("Post criado com sucesso!");
      }

      setHasChanges(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      toast.error("Erro ao salvar post", {
        description: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b gap-4 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            {onCancel && (
              <Button variant="ghost" size="icon" onClick={onCancel}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <CardTitle className="text-lg sm:text-xl">{initialPost ? "Editar Post" : "Criar Novo Post"}</CardTitle>
          </div>
          {hasChanges && (
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs">
              Alterações não salvas
            </Badge>
          )}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto">
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6 pt-4 sm:pt-6 px-4 sm:px-6">
        {/* Cover Image */}
        <div className="space-y-2">
          <Label>Imagem de Capa</Label>
          {coverImageUrl && (
            <div className="relative w-full h-48 rounded-lg overflow-hidden mb-2">
              <img 
                src={coverImageUrl} 
                alt="Capa" 
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <Input
            value={coverImageUrl}
            onChange={(e) => {
              setCoverImageUrl(e.target.value);
              setHasChanges(true);
            }}
            placeholder="https://exemplo.com/imagem.jpg"
            type="url"
          />
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label>Categoria</Label>
          {isCustomCategory ? (
            <div className="flex gap-2">
              <Input
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setHasChanges(true);
                }}
                placeholder="Digite a categoria"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCustomCategory(false)}
              >
                Cancelar
              </Button>
            </div>
          ) : (
            <Select 
              value={category} 
              onValueChange={(value) => {
                if (value === "__new__") {
                  setIsCustomCategory(true);
                  setCategory("");
                } else {
                  setCategory(value);
                }
                setHasChanges(true);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
                <SelectItem value="__new__" className="text-primary font-medium">
                  + Criar nova categoria
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label>Título</Label>
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Digite o título do artigo"
            className="text-2xl font-bold h-auto py-3"
          />
        </div>

        {/* Meta Description */}
        <div className="space-y-2">
          <Label>Meta Descrição (SEO)</Label>
          <Textarea
            value={metaDescription}
            onChange={(e) => {
              setMetaDescription(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Breve descrição para mecanismos de busca (max 160 caracteres)"
            maxLength={160}
            rows={2}
          />
          <p className="text-xs text-muted-foreground">
            {metaDescription.length}/160 caracteres
          </p>
        </div>

        {/* WYSIWYG Content Editor */}
        <div className="space-y-2">
          <Label>Conteúdo do Artigo</Label>
          <WYSIWYGEditor
            content={content}
            onChange={(html) => {
              setContent(html);
              setHasChanges(true);
            }}
          />
        </div>

        {/* Keywords */}
        <div className="space-y-2">
          <Label>Keywords (Palavras-chave)</Label>
          <div className="flex gap-2">
            <Input
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddKeyword();
                }
              }}
              placeholder="Digite uma palavra-chave e pressione Enter"
            />
            <Button type="button" onClick={handleAddKeyword} variant="outline">
              Adicionar
            </Button>
          </div>
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {keywords.map((keyword) => (
                <Badge key={keyword} variant="secondary" className="pl-3 pr-1">
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label>Status de Publicação</Label>
          <RadioGroup 
            value={status} 
            onValueChange={(value: any) => {
              setStatus(value);
              setHasChanges(true);
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="draft" id="draft" />
              <Label htmlFor="draft" className="font-normal cursor-pointer">
                Rascunho
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pending_review" id="pending_review" />
              <Label htmlFor="pending_review" className="font-normal cursor-pointer">
                Aguardando Revisão
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="published" id="published" />
              <Label htmlFor="published" className="font-normal cursor-pointer">
                Publicado
              </Label>
            </div>
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
}
